/* eslint-disable @typescript-eslint/typedef */
import {AfterContentInit, Component, HostBinding, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {Store} from '@ngrx/store';
import {AuthNgrx} from '~model/ngrx/auth-ngrx';
import {IconRegistryService} from '~core/utils/icon-registry.service';
import {AuthService} from '~core/security/auth.service';
import {CoreService} from '~core/background/core.service';
import {LoggerService} from '~core/utils/logger.service';
import {AppState, AppStore} from './app.store';
import {BehaviorSubject, combineLatest, Observable, Subscription} from 'rxjs';
import {MatSidenav} from '@angular/material/sidenav';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {DatePipe} from '@angular/common';
import {ParamService} from '~core/utils/param.service';
import {ReduxService} from '~core/utils/redux.service';
import {DateUtil} from '~utils/date-util';
import {filter, skip, debounceTime, map, startWith} from 'rxjs/operators';
import {Paths} from '~core/security/auth.roles';
import {MonitoramentoService} from '~core/api/monitoramento.service';
import {MatCalendarCellClassFunction} from '@angular/material/datepicker';
import {MatSnackBar} from '@angular/material/snack-bar';
import {ConfigService} from '~core/utils/config.service';
import {setMenuOpened} from '~feature/feature-app/feature-app.actions';
import {TranslateService} from '@ngx-translate/core';
import {ChatService} from '~core/api/chat.service';
import { assetUrl } from 'src/single-spa/asset-url';
import {MatIconRegistry} from '@angular/material/icon';
import {DomSanitizer} from '@angular/platform-browser';
import {loadLogo, loadPalette} from '~feature/feature-app/feature-app.selectors';
import {lightColorGen} from './theming.service';
import {LSItem} from '~utils/storage-utils';
import { AlertaService } from '~core/api/alerta.service';
import { AppConfigService } from '~core/background/app-config.service';
import { JwtHelperService } from '@auth0/angular-jwt';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [AppStore, IconRegistryService, CoreService, ChatService, AlertaService, AppConfigService],
})
export class AppComponent implements OnInit, AfterContentInit, OnDestroy {

  @ViewChild('Sidenav') sidenav: MatSidenav | undefined;
  versaoState = this.state.select(state => state.version);
  descriptionState!: Observable<string>;
  carregandoState = this.state.select(state => state.carregando);
  exibirLayouState = this.state.select(state => state.exibirLayout);
  relogioTempoState = this.state.select(state => state.relogioTempo);
  menuExpandidoState = this.state.select(state => state.menuEstaExpandido);
  profileUrlState = this.state.select(state => state.profileUrl);
  logoState = this.state.select(state => {
    localStorage.setItem(LSItem.linkLogo, state.logo ?? '');
    return state.logo;
  });
  filtros = this.state.select(state => state.filtros);
  state$: Observable<AppState> = this.state.select(state => state);
  showMainLayout$!: Observable<boolean>;
  isPrintRoute$!: Observable<boolean>;

  // Propriedades necessárias para o app-toolbar-filtro
  searchFormGroup!: FormGroup;

  format: string = 'HH:mm:ss - dd/MM/yyyy';
  formDate: FormGroup<{ date: FormControl<Date | null> }> = new FormGroup({
    date: new FormControl<Date | null>(null),
  });
  sidenavExpanse = new BehaviorSubject(false);
  hideSidenav = new BehaviorSubject<boolean>(false);
  menuOpened = new BehaviorSubject<string>('');
  activeMenu = new BehaviorSubject<'main' | 'settings'>(
    (localStorage.getItem('activeMenu') as 'main' | 'settings') || 'main'
  );
  private _isAdminUser: boolean = false;
  @HostBinding('style.--main-menu-vertical-width')
  menuVerticalWitdh: string = '6rem';
  @HostBinding('style.--main-height')
  mainHeight: string = '3rem';

  GRID_PATH: string = Paths.GRID;
  FINANCEIRO_PATH: string = Paths.FINANCEIRO;
  IMPORTACAO_PATH: string = Paths.IMPORTACAO;
  SEGURANCA_PATH: string = Paths.SEGURANCA;
  CHAT_PATH: string = Paths.CHAT;
  CONFIG_PATH: string = Paths.CONFIG;

  private datas: string[] = [];
  minDate: Date | null = null;
  maxDate: Date | null = null;
  actualDate!: Date;
  alertesPadding: number = 0;
  ativaTratativasIncidentes:boolean = false;

  private dataSub?: Subscription;


  constructor(
    private readonly iconsService: IconRegistryService,
    private readonly store: Store<AuthNgrx>,
    private readonly state: AppStore,
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly chatService: ChatService,
    private readonly coreService: CoreService,
    private readonly alertaService: AlertaService,
    private readonly appConfig: AppConfigService,
    private readonly monitoramentoService: MonitoramentoService,
    private readonly loggerService: LoggerService,
    private readonly fb: FormBuilder,
    private readonly datePipe: DatePipe,
    private readonly paramsService: ParamService,
    private readonly reduxService: ReduxService,
    private readonly snackbar: MatSnackBar,
    private readonly configService: ConfigService,
    private readonly translate: TranslateService,
    private readonly matIconRegistry: MatIconRegistry,
    private readonly domSanitizer: DomSanitizer,
    private readonly jwt: JwtHelperService
  ) {
    // Registrar ícone selected_calendar manualmente para debug
    this.matIconRegistry.addSvgIcon(
      'selected_calendar_manual',
      this.domSanitizer.bypassSecurityTrustResourceUrl(assetUrl('material/new_icons/selected_calendar.svg'))
    );
  }

  private isPublicRoute(url: string): boolean {
    return url.startsWith('/portal-cliente');
  }

  get roles(): BehaviorSubject<{ [k: string]: boolean }> {
    return this.state.roles;
  }

  get authState(): Observable<AuthNgrx> {
    return this.state.authState;
  }

  ngOnDestroy(): void {
    this.dataSub?.unsubscribe();
  }

  /**
   * Verifica se o usuário atual é Admin
   * Admins só tem acesso ao menu de configurações
   */
  get isAdminUser(): boolean {
    return this._isAdminUser;
  }

  ngOnInit(): void {
    this.configService.setUp();
    this.loggerService.log(`[Monitoramento] raw: ${this.state.getState().raw}`);
    this.state.select(state => state.logo);

    this.iconsService.setUp();

    const isPortalCliente$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => this.isPublicRoute(event.url)),
      startWith(this.isPublicRoute(this.router.url))
    );

    this.showMainLayout$ = combineLatest([
      this.authState.pipe(map(auth => !!auth.token)),
      isPortalCliente$,
    ]).pipe(
      map(([isAuthenticated, isPortal]) => isAuthenticated && !isPortal)
    );

    this.isPrintRoute$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.url.includes('(print:')),
      startWith(this.router.url.includes('(print:'))
    );

    this.state.inicializar();
    this.authService.inicializar();

    // Inicializar menuOpened com a URL atual
    this.menuOpened.next(this.router.url);

    // Verificar se o menu de configurações está ativo e navegar para /configuracoes/sistema
    // APENAS se a URL atual é '/' ou vazia (não redirecionar se já está em uma página de configurações)
    const activeMenuState = this.activeMenu.getValue();
    const currentUrl = this.router.url;
    const isConfigUrl = currentUrl.startsWith('/configuracoes');
    if (activeMenuState === 'settings' && !isConfigUrl && (currentUrl === '/' || currentUrl === '')) {
      this.router.navigate(['/configuracoes/sistema']);
      this.menuOpened.next('/configuracoes/sistema');
    }

    // Inicializar subscriptions para sincronização das rotas no AppStore
    this.subscribeRotas();

    // Inicializar FormGroup para pesquisa e filtros (seguindo padrão do GridComponent)
    this.searchFormGroup = this.fb.group({
      campoPesquisa: [this.state.getState().inputFiltro],
      statusFilterForm: ['Todos'],
    });

    // Desabilitar inicialmente o campo de pesquisa até dados carregarem
    this.searchFormGroup.get('campoPesquisa')?.disable();

    // Ativar funcionalidades de pesquisa e filtros
    this.onInputPesquisa();
    this.onStatusChange();

    // Habilitar campo de pesquisa quando dados estiverem carregados
    this.state$
      .pipe(filter(r => r.loaded))
      .subscribe(_ => {
        this.searchFormGroup.get('campoPesquisa')?.enable();
      });

    this.store.select(loadPalette).subscribe(color => this.state.updateProfileUrl(color, lightColorGen(color)));
    this.store.select(loadLogo).subscribe(logo => this.state.updateLogo(logo));
    this.authService.authNgrx$.subscribe(auth => this.state.setAuth(auth));


    this.searchFormGroup.get('campoPesquisa')?.disable();
    this.state$
      .pipe(filter(r => r.loaded))
      .subscribe(_ => {
        this.searchFormGroup.get('campoPesquisa')?.enable();
      });

    this.dataSub = this.paramsService.data$.subscribe(_ => {
      const date = this.paramsService.dateMonitoramento;
      const isNow = this.paramsService.isNow;
      this.state.updateRelogio(date, isNow);
    });

    this.authState.pipe(skip(1)).subscribe(auth => {
      this.chatService.currentUsername$.next(auth.nickname || '');

      // DEBUG: Visualizar permissões de ConfiguracoesType
      console.log('=== [APP.COMPONENT] AUTH STATE ===');
      console.log('auth:', auth);
      console.log('auth.roles:', auth.roles);
      console.log('auth.tokenRules:', auth.tokenRules);
      if (auth.tokenRules) {
        try {
          const decoded = this.jwt.decodeToken(auth.tokenRules);
          console.log('Token decodificado:', decoded);
          console.log('Grupo:', decoded?.grupo);
          console.log('ConfiguracoesType:', decoded?.grupo?.configuracoes);
        } catch (e) {
          console.log('Erro ao decodificar token:', e);
        }
      }
      console.log('==================================');

      // Verificar se usuário é Admin e forçar menu de configurações
      const isAdmin = auth.roles?.includes('Admin') || false;
      this._isAdminUser = isAdmin;

      if (isAdmin) {
        this.activeMenu.next('settings');
        localStorage.setItem('activeMenu', 'settings');

        // Se não está em página de configurações, redirecionar
        if (!this.router.url.startsWith('/configuracoes')) {
          this.router.navigate(['/configuracoes/sistema']);
          this.menuOpened.next('/configuracoes/sistema');
        }
      } else {
        const currentUrlSPA = window.location.pathname;
        // Do not force redirect if current route is public


        // Usuário não é admin - SEMPRE forçar menu principal e ir para /grid
        this.activeMenu.next('main');
        localStorage.setItem('activeMenu', 'main');

        // Forçar redirecionamento para /grid sempre que não for admin
        // Usa setTimeout para garantir que a navegação ocorra após o ciclo atual
        if (!this.isPublicRoute(currentUrlSPA)) {
          setTimeout(() => {
            this.router.navigate(['/grid']);
            this.menuOpened.next('/grid');
          }, 0);
        }

      }

      this.coreService.inicializar();
      this.coreService.carregando$.subscribe(res => this.state.setCarregando(res));

      this.coreService.exibirLayout$.subscribe(res => this.state.setExibirLayout(res));
      this.descriptionState = this.translate.get(this.state.getState().description);

      //! permite esconder o menu lateral conforme a opção: padrão 'mapa'
      this.router.events.pipe(
        filter(e => e instanceof NavigationEnd),
        map(e => e as NavigationEnd)
      ).subscribe((event: NavigationEnd) => {
        this.menuOpened.next(event.url);
      });

      this.sidenavExpanse.subscribe(menuOpened => {
        this.menuVerticalWitdh = menuOpened ? '16rem' : '4rem';
        this.store.dispatch(setMenuOpened({ menuOpened }) as any);
      });

      this.onFormData();
    });
  }

  ngAfterContentInit(): void {
    this.coreService.buscarAtualizacao();
    this.authState.pipe(
      filter(a => !!a.token)
    ).subscribe(_ => this.buscaDatas(false));
  }

  onFormData(): void {
    this.format = this.paramsService.isNow ? 'HH:mm:ss - dd/MM/yyyy' : 'dd/MM/yyyy';
    this.actualDate = this.paramsService.dateMonitoramento;
    this.formDate = this.fb.group({
      date: new FormControl<Date | null>(this.actualDate),
    });


    this.formDate.get('date')?.valueChanges.pipe(
      filter(v => this.actualDate !== v)
    ).subscribe(valor => {
      this.actualDate = valor ?? new Date();
      const date = this.datePipe.transform(valor ?? new Date(), 'yyyy-MM-dd');
      this.paramsService.alterarData(date || '');
      this.state.updateRelogio(valor ?? new Date(), this.paramsService.isNow);
      this.format = this.paramsService.isNow ? 'HH:mm:ss - dd/MM/yyyy' : 'dd/MM/yyyy';
      this.coreService.carregando$.next(true);
      this.reduxService.reset(true);
      this.coreService.inicializar();
    });
  }

  hoje(): void {
    this.formDate.get('date')?.setValue(new Date());
  }

  filterData = (d: Date | null): boolean => {
    const now = DateUtil.now();
    if (this.datas && this.datas.length && d) {
      const data = this.datePipe.transform(d, 'yyyy-MM-dd') as string;
      const hoje = this.datePipe.transform(now, 'yyyy-MM-dd') as string;
      return this.datas.includes(data) || data === hoje;
    }
    now.setDate(now.getDate() + 5);
    const select = d || now;
    return select.getTime() <= now.getTime();
  };

  buscaDatas(showMsg?: boolean): void {
    const diff = 7 * (24 * 60 * 60 * 1000);
    this.monitoramentoService.getDatas().subscribe(res => {
      this.datas = res;
      const now = DateUtil.now();
      let message;
      if (this.datas && this.datas.length) {
        const min = new Date(this.datas[0]);
        const max = new Date(this.datas[this.datas.length - 1]);
        min.setTime((min.getTime() > now.getTime() ? now : min).getTime() - diff);
        max.setTime((max.getTime() < now.getTime() ? now : max).getTime() + diff);
        this.minDate = min;
        this.maxDate = max;
        message = 'Lista de datas atualizada';
      } else {
        this.minDate = null;
        this.maxDate = null;
        message = 'Não rotas para monitorar';
      }
      const text = message;
      if (showMsg) {
        setTimeout(() => this.snackbar.open(text, 'OK', { duration: 3000 }), 500);
      }
    });
  }

  dateClass: MatCalendarCellClassFunction<Date> = (d, view) => {
    if (view === 'month') {
      const data = this.datePipe.transform(d, 'yyyy-MM-dd') as string;
      const selectedDate = this.datePipe.transform(this.actualDate, 'yyyy-MM-dd') as string;

      // Se é a data selecionada, marca com 'selected-date-class'
      if (data === selectedDate) {
        return 'selected-date-class';
      }

      // Se está nas datas com dados, marca com 'background-alert'
      if (this.datas && this.datas.length && this.datas.includes(data)) {
        return 'background-alert';
      }
    }

    // Na view de meses (ano), destaca os meses que têm dados
    if (view === 'year' && this.datas && this.datas.length) {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const prefix = `${year}-${month}`;
      if (this.datas.some(data => data.startsWith(prefix))) {
        return 'background-alert';
      }
    }

    // Na view de anos, destaca os anos que têm dados
    if (view === 'multi-year' && this.datas && this.datas.length) {
      const yearStr = String(d.getFullYear());
      if (this.datas.some(data => data.startsWith(yearStr))) {
        return 'background-alert';
      }
    }

    return '';
  };

  isNow(): boolean {
    return this.paramsService.isNow;
  }

  logout(logoutTokenSession = false): void {
    this.authService.desativar(logoutTokenSession);
    this.paramsService.reset();
  }

  menuToggle(expandido?: boolean): void {
    this.state.setMenuExpandido(expandido);
  }

  irParaUrl(url: string): void {
    // Se estiver navegando para a página principal, mudar para o menu principal
    if (url === '/' || url === '/grid') {
      this.activeMenu.next('main');
      localStorage.setItem('activeMenu', 'main');
      this.menuOpened.next('/grid');
    }
    this.router.navigateByUrl(url).catch(e => this.loggerService.error('Não foi possivel navegar', e));
  }

  imgDefault(profile: HTMLImageElement): void {
    profile.src = assetUrl('./images/profile.png');
  }

  sidenavToggle(): void {
    this.sidenavExpanse.next(!this.sidenavExpanse.getValue());
  }

  checkRoles(path: string, roles: { [k: string]: boolean } | null): boolean {
    return !!roles && roles[path];
  }

  toggleSettingsMenu(): void {
    const currentMenu = this.activeMenu.getValue();
    const newMenu = currentMenu === 'main' ? 'settings' : 'main';
    this.activeMenu.next(newMenu);
    localStorage.setItem('activeMenu', newMenu);

    if (newMenu === 'settings') {
      this.router.navigate(['/configuracoes/sistema']);
      this.menuOpened.next('/configuracoes/sistema');
    }
  }

  openSettingsMenu(): void {
    this.activeMenu.next('settings');
    localStorage.setItem('activeMenu', 'settings');
    // Fecha o sidenav ao abrir o menu de configurações
    if (this.sidenavExpanse.getValue()) {
      this.sidenavExpanse.next(false);
    }
    this.router.navigate(['/configuracoes/sistema']);
    this.menuOpened.next('/configuracoes/sistema');
  }

  openMainMenu(): void {
    this.activeMenu.next('main');
    localStorage.setItem('activeMenu', 'main');
    this.router.navigate(['/grid']);
    this.menuOpened.next('/grid');
  }

  // Métodos para sincronização das rotas no AppStore
  private subscribeRotas(): void {
    // 🔥 SUBSCRIPTION ESPECÍFICA PARA LOADED STATE
    // Vamos apenas sincronizar o estado 'loaded' do GridStore para o AppStore
    // para que o toolbar-filtro apareça quando as rotas estiverem carregadas

    // Esta é uma workaround temporária até refatorarmos a arquitetura
    // TODO: Refatorar para uma arquitetura mais limpa sem dependência circular

    // Por enquanto, vou simular o loaded baseado no carregamento geral
    this.coreService.carregando$.subscribe(isLoading => {
      if (!isLoading) {
        // Quando não está mais carregando, marca como loaded
        this.state.updateRotasFiltradas([], true);
      }
    });
  }

  // Métodos auxiliares para filtros e pesquisa no app.component
  onInputPesquisa(): void {
    if (this.searchFormGroup.get('campoPesquisa')) {
      this.searchFormGroup.get('campoPesquisa')?.valueChanges.pipe(
        debounceTime(300)
      ).subscribe(value => {
        this.state.updateInputFilter(value ?? '');
      });
    }
  }

  onStatusChange(): void {
    if (this.searchFormGroup.get('statusFilterForm')) {
      this.searchFormGroup.get('statusFilterForm')?.valueChanges.pipe(
        debounceTime(300)
      ).subscribe(value => {
        // Atualizar o estado no AppStore para sincronização
        this.state.updateStatusFilter(value ?? 'Todos');
      });
    }
  }

  private refreshAlertCount(): void {
    this.alertaService.getAlertasPaginados({
      data: '',
      offset: 0,
      limit: 9,
      search: "",
      filter: "infoOnly",
    }).subscribe((val) => {
      this.alertesPadding = this.appConfig.getAtivaTratativasIncidentes() ? val.content.length : 0;
    });
  }
}
